import React from "react";
import { Button, Card } from "react-bootstrap";
import { BiData } from 'react-icons/bi';
import { BsCircleFill } from "react-icons/bs";
import { MdLock } from 'react-icons/md';
import MaterialTable, { MTableToolbar } from 'material-table';
import icons from 'MaterialTableIcons';
import { Link, useHistory } from 'react-router-dom';
import { contrastColor } from 'contrast-color';
import { engineLabels, EngineIcon, getSourceColor, getSourceLabel } from 'Utils';
import { INK, STATUS } from 'Utils/styleTokens';
import DiscoveryTooltip from "DiscoveryTooltip";
import { useCanEdit, READ_ONLY_TITLE } from 'Auth/permissions';
import { FILE_SOURCE_TYPE, isHiddenFromSourceList, isPlatformManaged, platformOwnerKind } from './datasourcesApi';

// Short display labels for FILE_* datasource types.
const FILE_TYPE_LABELS = {
  FILE_DELIMITED_FOLDER: 'Delimited',
  FILE_PARQUET_FOLDER: 'Parquet',
  FILE_GENERIC_FOLDER: 'Folder Search',
  FILE_DOCUMENT_FOLDER: 'Documents',
  FILE_JSON_FOLDER: 'JSON',
  FILE_XML_FOLDER: 'XML',
  FILE_EXCEL_FOLDER: 'Excel',
  FILE_FIXED_WIDTH_FOLDER: 'Fixed width',
  FILE_HL7_FOLDER: 'HL7',
  FILE_EDI_FOLDER: 'EDI',
  FILE_SQL_DUMP_FOLDER: 'SQL dump',
};

const IMPORTED_LOCAL_COLOR = INK[500];
const IntegrationSourceBadge = ({ registeredServiceId, registeredServiceName, managedBy, needsCredentials, readOnly }) => {
  // Provenance is permanent: the badge stays after adoption (managedBy === 'c2'), only its
  // tooltip downgrades from the call-to-action to a plain origin label.
  const isIntegrationImport = !!registeredServiceId;
  const isImportedLocal = !registeredServiceId && needsCredentials === true;
  if (!isIntegrationImport && !isImportedLocal) return null;
  // Adopted = explicit marker OR credentials stored (needsCredentials cleared) — matches
  // isImportedUnadopted in datasourcesApi.
  const isAdopted = isIntegrationImport && (managedBy === 'c2' || needsCredentials !== true);
  const color = isImportedLocal ? IMPORTED_LOCAL_COLOR : getSourceColor(registeredServiceId);
  const label = isImportedLocal ? 'Local' : (registeredServiceName || getSourceLabel(registeredServiceId));
  // C2D-1114: don't tell the user to "save credentials" on a read-only source — non-admins can't,
  // and the copy was misleading. Read-only unadopted imports get an admin-scoped message instead.
  const tooltip = isImportedLocal
    ? (readOnly ? 'Local source — only an administrator can supply credentials to enable it.' : 'Save credentials to enable.')
    : isAdopted
      ? `Imported from ${label}; now managed by C2.`
      : readOnly
        ? 'Read-only row synced from the integration — only an administrator can supply connection details to adopt it.'
        : 'Read-only row synced from the integration. Save credentials to adopt it.';
  return (
    <DiscoveryTooltip placement='top' text={tooltip}>
      <span
        className='ml-2'
        style={{
          backgroundColor: color,
          color: contrastColor({ bgColor: color }),
          padding: '2px 8px',
          borderRadius: 12,
          fontSize: '0.75rem',
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}
      >
        {label} (imported)
      </span>
    </DiscoveryTooltip>
  );
};

/** TDM-owned row: the staging area's own source or a subset's raw view. Edited from the TDM screens. */
const PlatformManagedBadge = ({ connection }) => {
  const kind = platformOwnerKind(connection);
  const label = kind === 'staging' ? 'TDM staging' : kind === 'raw-view' ? 'TDM raw view' : 'Platform managed';
  return (
    <DiscoveryTooltip placement='top' text='Managed by Test Data Management: settings and credentials live on the staging area / subset. Not shown on the landscapes.'>
      <span
        className='ml-2'
        data-testid='platform-managed-badge'
        style={{
          backgroundColor: '#EEF1F6', color: '#5C6473', padding: '2px 8px', borderRadius: 12,
          fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center',
        }}
      >
        {label}
      </span>
    </DiscoveryTooltip>
  );
};

const ReadOnlyBadge = () => (
  <DiscoveryTooltip placement='top' text='Read Only: only admins can edit this source or run masking/encryption jobs against it.'>
    <span
      className='ml-2'
      style={{
        backgroundColor: INK[500],
        color: '#fff',
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: '0.75rem',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <MdLock style={{ fontSize: '0.85rem' }} />
      Read Only
    </span>
  </DiscoveryTooltip>
);

const DataSourceNameColumn = (props) => {
  // The name always opens the source editor — for native and imported rows alike. Imported
  // sources previously led to their risk profile / run history, but that was surprising: the
  // editor is where you inspect and configure the source, and the risk profile is still reachable
  // from the discovery / landscape views. An unadopted import (needsCredentials) especially must
  // land in the editor so the user can supply the connection details / password (C2D-1114).
  const imported = !!props.registeredServiceId;
  const needsConfig = imported && props.needsCredentials === true;
  const editorTarget = `/sources/source-editor/${props.dataSourceId != null ? props.dataSourceId : props.name}`;
  const tooltip = needsConfig
    ? 'Configure this imported source — enter connection details'
    : 'View data source details';
  return (
    <div className="truncate-ellipsis pl-3 pt-3 pb-3">
      <DiscoveryTooltip text={tooltip} placement='top'>
        <Link to={editorTarget}>
          {props.name}
        </Link>
      </DiscoveryTooltip>
    </div>
  );
};

const StatusCell = ({ status, statusMessage }) => {
  const statusMap = {
    'FAILED': 'Failed.',
    'ERROR': 'Failed.',
    'SUCCESS': 'Succeeded.',
    'SUCCEEDED': 'Succeeded.'
  };

  const statusColors = {
    'FAILED': STATUS.danger.dot,
    'ERROR': STATUS.danger.dot,
    'SUCCESS': STATUS.success.dot,
    'SUCCEEDED': STATUS.success.dot
  };

  return (
    <DiscoveryTooltip text={statusMessage} placement='top'>
      <div className='text-center d-flex align-items-center justify-content-center'>
        {statusColors[status] && (<BsCircleFill className='mr-2' color={statusColors[status]} />)}
        {statusMap[status] ? statusMap[status] : '-'}
      </div>
    </DiscoveryTooltip>
  );
};

const getMonth = (date) => {
  const dateOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
  return (new Date(date)).toLocaleDateString('en-US', dateOptions);
};

const getTime = (date) => {
  const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
  return (new Date(date)).toLocaleString('en-US', timeOptions);
};

// Returns the human-readable parent label for a data source row.
function getParentLabel(row, connections) {
  if (row.registeredServiceId) {
    return row.registeredServiceName || getSourceLabel(row.registeredServiceId);
  }
  if (row.parentDataSourceId != null) {
    const parent = (connections || []).find((c) => c.id === row.parentDataSourceId);
    return parent ? parent.name : `Source #${row.parentDataSourceId}`;
  }
  return '';
}

// Returns the display label for the type column (used for filtering too).
function getTypeLabel(row) {
  if (row.type === FILE_SOURCE_TYPE) {
    return FILE_TYPE_LABELS[row.datasourceType] || row.datasourceType || 'File';
  }
  return engineLabels[row.type] || 'JDBC';
}

const DataSourcesTable = (props) => {
  const tableRef = React.createRef();
  const history = useHistory();
  const canEdit = useCanEdit();
  // Sources tagged landscape=none (TDM staging sources and their raw views) are not listed here
  // at all; everything else that reads props.connections is unaffected.
  const allConnections = props.connections;
  const connections = React.useMemo(
    () => (allConnections || []).filter((c) => !isHiddenFromSourceList(c)),
    [allConnections]
  );

  // Sub-sources (parentDataSourceId set — spawned from a folder search candidate, the
  // profile flow, or "Discover everything" auto fan-out) are hidden from the top-level
  // table by default so a large fan-out doesn't flood the list; the toggle reveals them
  // and the Parent column then says where each came from. Everything else (coverage
  // rules, masking pickers, boards) still reads the unfiltered connections array.
  const [showSubSources, setShowSubSources] = React.useState(false);
  const subSourceCount = React.useMemo(
    () => connections.filter((r) => r.parentDataSourceId != null).length,
    [connections]
  );
  const childCountById = React.useMemo(() => {
    const map = new Map();
    connections.forEach((r) => {
      if (r.parentDataSourceId != null) {
        map.set(r.parentDataSourceId, (map.get(r.parentDataSourceId) || 0) + 1);
      }
    });
    return map;
  }, [connections]);

  // Native + adopted rows first, anything awaiting credentials last.
  const sortedRows = React.useMemo(() => {
    const rows = showSubSources
      ? connections
      : connections.filter((r) => r.parentDataSourceId == null);
    const awaitingCreds = (r) => r.needsCredentials === true;
    return [...rows].sort((a, b) => Number(awaitingCreds(a)) - Number(awaitingCreds(b)));
  }, [connections, showSubSources]);

  const COLUMNS = [
    {
      title: 'NAME',
      field: 'name',
      headerStyle: { fontFamily: 'Rubik' },
      cellStyle: { fontFamily: 'Rubik' },
      render: (rowData) => (
        <div className='d-flex align-items-center'>
          <DataSourceNameColumn
            dataSourceId={rowData.id}
            name={rowData.name}
            registeredServiceId={rowData.registeredServiceId}
            needsCredentials={rowData.needsCredentials}
          />
          <IntegrationSourceBadge
            registeredServiceId={rowData.registeredServiceId}
            registeredServiceName={rowData.registeredServiceName}
            managedBy={rowData.managedBy}
            needsCredentials={rowData.needsCredentials}
            readOnly={rowData.readOnly}
          />
          {rowData.readOnly && <ReadOnlyBadge />}
          {isPlatformManaged(rowData) && <PlatformManagedBadge connection={rowData} />}
          {childCountById.get(rowData.id) > 0 && (
            <span
              title='Sub data sources spawned from this source — see the Sub-sources panel in its editor'
              style={{
                marginLeft: 8, fontFamily: 'Rubik', fontSize: 11, borderRadius: 999,
                background: '#EEF1F6', color: '#5C6473', padding: '2px 8px', whiteSpace: 'nowrap',
              }}
            >
              {childCountById.get(rowData.id)} sub-source{childCountById.get(rowData.id) > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )
    },
    {
      title: 'TYPE',
      field: 'type',
      headerStyle: { fontFamily: 'Rubik' },
      cellStyle: { fontFamily: 'Rubik' },
      customFilterAndSearch: (term, rowData) =>
        getTypeLabel(rowData).toLowerCase().includes((term || '').toLowerCase()),
      render: (rowData) => {
        if (rowData.type === FILE_SOURCE_TYPE) {
          const engine = (rowData.vfsScheme || 'LOCAL').toLowerCase();
          return (
            <span>
              <EngineIcon engine={engine} classes='mr-2' size='1.1rem' />
              {FILE_TYPE_LABELS[rowData.datasourceType] || rowData.datasourceType || 'File'}
            </span>
          );
        }
        return (
          <span>
            <EngineIcon engine={rowData.type} classes='mr-2' size='1.1rem' />
            {engineLabels[rowData.type] || 'JDBC'}
          </span>
        );
      }
    },
    {
      title: 'PARENT',
      field: 'parentDataSourceId',
      headerStyle: { fontFamily: 'Rubik' },
      cellStyle: { fontFamily: 'Rubik' },
      customFilterAndSearch: (term, rowData) =>
        getParentLabel(rowData, allConnections).toLowerCase().includes((term || '').toLowerCase()),
      render: (rowData) => {
        if (rowData.registeredServiceId) {
          return (
            <span style={{ fontFamily: 'Rubik', color: INK[700] }}>
              {rowData.registeredServiceName || getSourceLabel(rowData.registeredServiceId)}
            </span>
          );
        }
        if (rowData.parentDataSourceId != null) {
          // Full list: a listed child may hang off a parent this table hides.
          const parent = (allConnections || []).find((c) => c.id === rowData.parentDataSourceId);
          const label = parent ? parent.name : `Source #${rowData.parentDataSourceId}`;
          return parent ? (
            <Link to={`/sources/source-editor/${encodeURIComponent(parent.id)}`}>
              {label}
            </Link>
          ) : (
            <span style={{ color: INK[400] }}>{label}</span>
          );
        }
        return null;
      }
    },
    {
      title: 'LAST TEST',
      field: 'status',
      filtering: false,
      width: 200,
      headerStyle: { fontFamily: 'Rubik', textTransform: 'uppercase', textAlign: 'center' },
      cellStyle: { padding: 0, fontFamily: 'Rubik' },
      render: (rowData) => (
        <StatusCell status={rowData.status} statusMessage={rowData.statusMessage} />
      )
    },
    {
      title: 'DATE TESTED',
      field: 'creationTime',
      filtering: false,
      headerStyle: { fontFamily: 'Rubik', textAlign: 'center' },
      cellStyle: { fontFamily: 'Rubik', textAlign: 'center' },
      render: ({ lastTested }) => {
        if (lastTested) {
          return <span>{getMonth(lastTested)} @ {getTime(lastTested)}</span>;
        }
        return <span>-</span>;
      }
    },
  ];

  return (
    <Card className="entity-panel-table">
      <MaterialTable
        components={{
          Toolbar: (toolbarProps) => (
            <div className="entity-panel-table-header d-flex">
              <div className="text-left widget-header p-3">
                <BiData className="mr-2 widget-header-icon" />
                <span>Create / manage data sources </span>
              </div>
              <div className='d-flex flex-grow-1 justify-content-end'>
                {subSourceCount > 0 && (
                  <div className='pt-3 pr-3' style={{ whiteSpace: 'nowrap' }}>
                    <label
                      style={{ fontFamily: 'Rubik', fontSize: 13, color: '#5C6473', cursor: 'pointer', marginBottom: 0 }}
                      data-testid='show-sub-sources-toggle'
                    >
                      <input
                        type='checkbox'
                        checked={showSubSources}
                        onChange={(e) => setShowSubSources(e.target.checked)}
                        style={{ marginRight: 6, verticalAlign: 'middle' }}
                      />
                      Show sub-sources ({subSourceCount})
                    </label>
                  </div>
                )}
                <div><MTableToolbar {...toolbarProps} /></div>
                <div className='pt-2 pr-2'>
                  <Button
                    variant='primary'
                    id='data-sources-add'
                    data-testid="add-source-dropdown"
                    disabled={!canEdit}
                    title={!canEdit ? READ_ONLY_TITLE : undefined}
                    onClick={() => history.push('/sources/source-editor')}
                  >
                    + ADD
                  </Button>
                </div>
              </div>
            </div>
          )
        }}
        tableRef={tableRef}
        icons={icons}
        columns={COLUMNS}
        data={sortedRows}
        isLoading={props.fetchingData}
        options={{
          showTitle: false,
          pageSize: 10,
          filtering: true,
        }}
      />
    </Card>
  );
};

export default DataSourcesTable;
